import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewCard from "@/components/ReviewCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Review } from "@/lib/mockData";

const ReviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const commentId = searchParams.get("comment");
  const { isAdmin } = useAuth();
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReview = async () => {
      if (!id) return;
      setLoading(true);
      const { data } = await supabase
        .from("reviews_public" as any)
        .select("*")
        .eq("id", id)
        .maybeSingle();
      const r = data as any;
      if (r) {
        setReview({
          id: r.id,
          type: r.type,
          title: r.title,
          organization: r.organization,
          location: r.location,
          rating: Number(r.rating),
          difficulty: Number(r.difficulty),
          wouldDoAgain: r.would_do_again ?? true,
          review: r.review,
          author: r.author,
          date: r.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
          tags: r.tags || [],
          thumbsUp: r.thumbs_up || 0,
          thumbsDown: r.thumbs_down || 0,
          major: r.major || "",
          startDate: r.start_date ?? null,
          endDate: r.end_date ?? null,
          userId: null,
          isOwner: !!r.is_owner,
          compensation: r.compensation ?? null,
          interviewInfo: r.interview_info ?? null,
          responsibilities: r.responsibilities ?? null,
          experienceKey: r.experience_key ?? null,
          isPioneer: !!r.is_pioneer,
        } as Review);
      } else {
        setReview(null);
      }
      setLoading(false);
    };
    fetchReview();
  }, [id]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="container max-w-2xl py-8 flex-1">
        <Link
          to="/activity"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        {loading ? (
          <p className="text-sm text-muted-foreground py-12 text-center">Loading review...</p>
        ) : !review ? (
          <p className="text-sm text-muted-foreground py-12 text-center">
            This review is no longer available.
          </p>
        ) : (
          <ReviewCard
            review={review}
            index={0}
            isAdmin={isAdmin}
            defaultCommentsOpen={!!commentId}
            highlightCommentId={commentId}
          />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ReviewPage;
